import React, { useState } from 'react';
import { ArrowRight, Heart, Activity, Brain, Users, Briefcase, Building2, Church, TrendingUp, BookOpen, Sparkles, PrayingHands, CloudSun, Columns3, Zap } from 'lucide-react';
import { personalGrowth, spheresOfInfluence, topicsContent } from '../data/mock';

const iconMap = {
  Heart: Heart,
  Activity: Activity,
  Brain: Brain,
  Users: Users
};

const sphereIcons = {
  Work: Briefcase,
  Business: Building2,
  Ministry: Church,
  Investments: TrendingUp,
  Resources: BookOpen
};

const topicIcons = {
  Meditation: Sparkles,
  Prayer: Heart,
  Angels: CloudSun,
  '7 Pillars of Wisdom': Columns3,
  Power: Zap
};

const Features = () => {
  const [activeTab, setActiveTab] = useState('personal');

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1 bg-indigo-50 text-indigo-600 rounded-full text-sm font-medium mb-4">
            Comprehensive Resources
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Everything you need for
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent"> spiritual growth</span>
          </h2>
          <p className="text-lg text-slate-600">
            Reduce confusion, grow deeper, and live your faith more effectively with our integrated, biblically-grounded platform.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {['personal', 'spheres', 'topics'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                activeTab === tab
                  ? 'bg-slate-900 text-white shadow-lg'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab === 'personal' && 'Personal Growth'}
              {tab === 'spheres' && 'Spheres of Influence'}
              {tab === 'topics' && 'Core Topics'}
            </button>
          ))}
        </div>

        {/* Personal Growth */}
        {activeTab === 'personal' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 animate-in fade-in duration-500">
            {personalGrowth.map((item, index) => {
              const Icon = iconMap[item.icon] || Heart;
              return (
                <div
                  key={item.title}
                  className="group p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className={`w-14 h-14 rounded-2xl mb-4 flex items-center justify-center transition-all duration-300 ${
                    index === 0 ? 'bg-gradient-to-br from-rose-400 to-pink-500' :
                    index === 1 ? 'bg-gradient-to-br from-emerald-400 to-teal-500' :
                    index === 2 ? 'bg-gradient-to-br from-violet-400 to-purple-500' :
                    'bg-gradient-to-br from-amber-400 to-orange-500'
                  } group-hover:scale-110`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                  <a href={`#${item.title.toLowerCase()}`} className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-700">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              );
            })}
          </div>
        )}

        {/* Spheres of Influence */}
        {activeTab === 'spheres' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 animate-in fade-in duration-500">
            {spheresOfInfluence.map((sphere, index) => {
              const Icon = sphereIcons[sphere.title] || Briefcase;
              return (
                <div
                  key={sphere.title}
                  className="group p-6 bg-white border border-slate-200 rounded-2xl hover:border-indigo-300 hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{sphere.title}</h3>
                  <p className="text-sm text-slate-500 mb-3">{sphere.description}</p>
                  <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                    {sphere.stats}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Topics */}
        {activeTab === 'topics' && (
          <div className="space-y-4 animate-in fade-in duration-500">
            {topicsContent.map((topic, index) => {
              const Icon = topicIcons[topic.name] || Sparkles;
              return (
                <div
                  key={topic.name}
                  className="group flex flex-col md:flex-row md:items-center gap-4 p-6 bg-white border border-slate-200 rounded-2xl hover:border-indigo-300 hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                    index % 5 === 0 ? 'bg-gradient-to-br from-violet-400 to-purple-500' :
                    index % 5 === 1 ? 'bg-gradient-to-br from-rose-400 to-pink-500' :
                    index % 5 === 2 ? 'bg-gradient-to-br from-sky-400 to-blue-500' :
                    index % 5 === 3 ? 'bg-gradient-to-br from-amber-400 to-orange-500' :
                    'bg-gradient-to-br from-emerald-400 to-teal-500'
                  }`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">{topic.name}</h3>
                    <p className="text-slate-600 text-sm">{topic.description}</p>
                  </div>
                  <div className="md:text-right">
                    <p className="text-sm text-slate-400 italic">&ldquo;{topic.verse}&rdquo;</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all hidden md:block" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default Features;
